import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Users, Layers, ShieldCheck, DoorOpen, Snowflake, Mountain } from "lucide-react";
import { SEO } from "@/components/SEO";

const SECTIONS = [
  {
    icon: Users,
    heading: "1. How much usable living space do you actually get?",
    body: [
      `Not all "sleeps two" claims are equal. The real question is: can one person be in bed while another sits comfortably below, or does the whole thing collapse into a single sleeping pod the moment it's set up?`,
      `A genuinely spacious build gives you a proper separation between an upstairs sleeping area and a downstairs living area — room to sit upright, cook, wait out rain, or just have a cup of tea without disturbing someone who's turned in early. A lot of cheaper campers are, underneath the marketing language, still just a standard-sized roof tent added on top of a canopy.`,
      `It matters even more at night: if the sleeping platform sits directly over the only way down, one person getting up for a toilet stop means waking the other too. Larger campers and motorhomes usually don't have this problem — ample space is their whole advantage — so this is really a question that separates budget-tier builds from the rest, not an argument against bigger, pricier options.`,
    ],
    ask: "What are the internal dimensions — and can two people be in different postures (one lying, one seated) at the same time?",
  },
  {
    icon: Layers,
    heading: "2. What's the tent actually made from?",
    body: [
      `Canvas quality is one of the biggest hidden cost-cutting areas in this category, because it's hard to judge from a photo.`,
    ],
    bullets: [
      <><span className="text-white">Fabric type and layers:</span> Premium canvas (like the dual-layered South African canvas we use) breathes properly, resists condensation, and holds up to years of UV and abrasion. Cheaper polyester tent fabrics can still be well made, but they're a different material with different longevity, breathability and feel.</>,
      <><span className="text-white">Mattress thickness:</span> This is an easy one to compare directly. 100mm vs 60mm is a real, noticeable difference in comfort on a multi-night trip — not a marginal one.</>,
    ],
    ask: `What's the fabric, specifically — not just whether it's "waterproof"? And what's the mattress thickness?`,
  },
  {
    icon: ShieldCheck,
    heading: `3. Is the frame actually proven, or just "TIG welded"?`,
    body: [
      `Almost every brand in this category will tell you their aluminium is TIG welded — that's a manufacturing method, not a quality guarantee. A better first clue to the frame's real engineering is the size of the side door openings, not the marketing spec sheet. Small openings are the safe, easy option structurally; openings the size of Bush Cabin's take an entirely different level of engineering to create while keeping the frame genuinely strong. Beyond that, what actually tells you something is:`,
    ],
    bullets: [
      <><span className="text-white">Warranty length on the frame specifically.</span> A longer structural warranty signals a manufacturer who's confident their welds, hinges and load points will still be sound years down the line. A shorter warranty is a different level of confidence.</>,
      <><span className="text-white">Track record.</span> Has this exact product been proven internationally over years of real use, or is it a new product line that's only just launched here?</>,
      <><span className="text-white">Fabrication quality.</span> Look closely at door openings, hinge points and seam finishing in person if you can. Cheaper fabrication often shows up first in tight tolerances around doors and closures.</>,
    ],
    ask: "What's the frame warranty specifically, and how long has this exact product been proven in the field?",
  },
  {
    icon: DoorOpen,
    heading: "4. How do you actually get in and out?",
    body: [
      `This one gets overlooked in spec sheets but matters enormously in daily use — especially if you're not 25 anymore, or you're camping in the rain.`,
      `There's a real difference between:`,
    ],
    bullets: [
      "A true rear door, where the tailgate is removed and replaced with an engineered subframe, giving you a proper walk-in opening at the back of the vehicle, versus",
      "A standard canopy-style rear door, where the factory tailgate stays in place and you climb in over it, then reach back to pull the tailgate up and the canopy door down from inside.",
    ],
    footer: `The first is a genuine living space you step into. The second is still, fundamentally, a ute canopy you're climbing into. This one's usually easy to spot from photos alone — look at the rear of the vehicle before you ask anything else.`,
  },
  {
    icon: Snowflake,
    heading: "5. Insulation, sealing and lighting — the things you don't notice until you're using it",
    body: [
      `Foam insulation, integrated quality lighting, and proper dust sealing don't photograph well, so they're easy for a budget build to skip or under-spec. But they're the difference between a camper that's genuinely comfortable in a Central Otago frost or on a dusty back-country road, and one that's fine in good weather but hard work outside it.`,
    ],
    ask: "If the product is new to market, what real-world testing — not just factory claims — has it had in NZ conditions? Dust ingress in particular often only shows up after real corrugated-road use.",
  },
  {
    icon: Mountain,
    heading: "6. Is the off-road capability genuine, or just gravel-road capability?",
    body: [
      `Driving on a well-formed gravel road isn't the same thing as 4WDing, but the two get confused constantly. Plenty of slide-on campers, and even ute-mounted motorhome-style builds, are marketed as off-road capable — and that's not because the sellers are being dishonest, it's usually because they don't have hands-on 4WD experience or expertise themselves, and haven't actually tested that claim on the kind of terrain it implies.`,
      `If genuine off-road capability matters to you — actual 4WD-only routes, not just a formed back-country road — ask what the build has really been driven on, and by people who know the difference.`,
    ],
    ask: "What specific 4WD-only routes has this exact build been driven on — not just formed gravel roads — and by someone with genuine 4WD experience?",
  },
];

const CHECKLIST = [
  "Can two people use the space at once — one lying down, one seated?",
  "What's the tent fabric, and how many layers?",
  "Mattress thickness?",
  "What's the frame warranty, specifically — and how long has this product actually been proven in the field?",
  "Does entry require removing the tailgate and using a real door, or climbing over the tailgate?",
  "Is it insulated, and is dust sealing tested in real NZ back-country conditions — or just claimed?",
  "Has this exact build actually been driven on 4WD-only routes — not just gravel roads — and by someone with genuine 4WD experience?",
  "What does the total build cost look like once you add the accessories you actually need?",
];

export default function UteCamperBuyersGuidePage() {
  return (
    <div data-testid="ute-camper-buyers-guide-page">
      <SEO
        path="/guides/ute-camper-buyers-guide"
        title="Ute Camper Buyer's Guide — What Separates An OEM-Grade Camper From A Budget One"
        description="A practical, no-nonsense guide to what actually matters when comparing ute campers in NZ: living space, canvas quality, frame warranty, rear door access, insulation and real total cost."
        noindex
      />

      <section className="container-x pt-32 pb-16 md:pt-40 md:pb-20">
        <p className="overline mb-4">— Buyer's Guide</p>
        <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-4xl">
          WHAT ACTUALLY SEPARATES AN <span className="text-[var(--brand-primary)]">OEM-GRADE CAMPER</span> FROM A BUDGET ONE.
        </h1>
        <p className="text-[var(--text-secondary)] text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
          The ute camper category in New Zealand has grown fast, and there are now several products on the market at very different price points. On the surface, a lot of these look similar in photos: aluminium canopy, pop-top or fold-out sleeping area, a tent fabric of some kind on top. But the difference between a genuine OEM-grade camper and a budget ute camper shows up fast once you look past the marketing photos.
        </p>
        <p className="text-[var(--text-secondary)] text-lg md:text-xl mt-4 max-w-2xl leading-relaxed">
          If you're weighing up options, here's what we'd actually check before signing up — because these are the things that determine whether the camper still feels solid in five years, and whether it's a pleasant place to spend a wet weekend in the back country, or just a place to sleep.
        </p>
      </section>

      <section className="container-x pb-16 md:pb-20">
        <div className="max-w-3xl space-y-14">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.heading} data-testid={`guide-section-${i}`} className="border-t border-[var(--border-default)] pt-8">
                <div className="flex items-start gap-4 mb-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 shrink-0 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-default)]">
                    <Icon size={20} className="text-[var(--brand-primary)]" strokeWidth={1.6} />
                  </span>
                  <h2 className="font-display text-2xl md:text-3xl text-white leading-tight pt-1.5">{s.heading}</h2>
                </div>
                {s.body.map((p, pi) => (
                  <p key={pi} className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-4 last:mb-0">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="mt-2 space-y-3">
                    {s.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                        <span className="mt-2.5 w-1.5 h-1.5 bg-[var(--brand-primary)] shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.footer && (
                  <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mt-4">{s.footer}</p>
                )}
                {s.ask && (
                  <p data-testid={`guide-ask-${i}`} className="mt-4 pl-4 border-l-2 border-[var(--brand-primary)] font-mono text-xs md:text-sm text-white/70 leading-relaxed">
                    <span className="text-[var(--brand-primary)] uppercase tracking-[0.15em]">Ask: </span>{s.ask}
                  </p>
                )}
              </div>
            );
          })}

          {/* PRICE / VALUE SPECTRUM */}
          <div data-testid="guide-section-price" className="border-t border-[var(--border-default)] pt-8">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4 leading-tight">7. What are you actually comparing on price?</h2>
            <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-4">
              It's tempting to compare campers purely on the sticker price, especially once you're already spending real money on a new vehicle build. But a budget ute camper and an OEM-grade camper aren't different tiers of the same product — they're different categories of product altogether. The cheaper one isn't simply a stripped-back version of the pricier one; it comes with genuine compromises attached.
            </p>
            <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-6">
              In the context of a full build on a new ute — wheels, tyres, suspension, canopy or camper, fit-out — you're already committing a meaningful budget either way. The real question isn't "what's the cheapest camper I can add," it's "which one actually delivers the vehicle I'll be happy with in five years."
            </p>
            <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-4">
              Budget campers can make genuine sense for an older vehicle, or if you're working to a tight budget — there's nothing wrong with matching the spend to the vehicle underneath it. Where it's worth pausing is a late-model or new ute.
            </p>
            <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-6">
              Once you've spent on wheels and tyres, suspension, electrics, water and the rest of a proper fit-out, the saving from choosing the cheaper camper becomes a relatively small slice of the total build cost. What you're left living with, day to day, are the compromises — and a build that's less likely to hold its value for the next buyer.
            </p>
            <div className="max-w-xl" data-testid="guide-value-spectrum">
              <div className="relative h-1.5 rounded-full bg-gradient-to-r from-white/15 via-white/30 to-[var(--brand-primary)]" />
              <div className="flex justify-between mt-3 font-mono text-[10px] md:text-xs uppercase tracking-[0.15em] text-[var(--text-tertiary)]">
                <span>Budget Ute Camper</span>
                <span className="text-[var(--brand-primary)]">OEM-Grade Camper</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-16 md:py-20">
          <p className="overline mb-4">— Before You Buy</p>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-8 max-w-2xl leading-[0.95]">
            QUICK CHECKLIST BEFORE YOU BUY.
          </h2>
          <ul className="max-w-2xl space-y-3">
            {CHECKLIST.map((c) => (
              <li key={c} data-testid="guide-checklist-item" className="flex items-start gap-3 text-base md:text-lg text-white leading-relaxed">
                <Check size={18} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="container-x py-16 md:py-24">
        <div className="border border-[var(--border-default)] p-8 md:p-12 max-w-3xl">
          <p className="text-white text-lg md:text-xl leading-relaxed">
            If you'd like to see how the Bush Cabin stacks up against any camper you're considering, get in touch — we're happy to talk you through it directly.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/bush-cabin#enquire" data-testid="guide-cta-enquire" className="btn-primary">
              Ask Us Anything <ArrowUpRight size={14} />
            </Link>
            <Link to="/bush-cabin" data-testid="guide-cta-view-bushcabin" className="btn-secondary">
              View The Bush Cabin
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
